import React, { useState, useEffect } from 'react';
import { Layout, Avatar, Button, Dropdown, Breadcrumb, Space, Typography, message, Tag, Drawer } from 'antd';
import {
  MenuOutlined, TagOutlined, InboxOutlined, CalculatorOutlined, ApartmentOutlined,
  ShopOutlined, ShoppingCartOutlined, ToolOutlined, BarChartOutlined, FileTextOutlined,
  FileDoneOutlined, PieChartOutlined, UserOutlined, SafetyOutlined, LogoutOutlined,
  QuestionCircleOutlined, HomeOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import { updateTheme as updateThemeApi } from '../../api/users.api';
import { MODULE_IDS } from '../../utils/constants';
import KeyboardShortcutsModal from '../common/KeyboardShortcutsModal';
import CommandPalette from '../common/CommandPalette';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import logo from '../../assests/logo.jpeg';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const THEME_COLORS = [
  { color: '#1a56db', name: 'Blue' },
  { color: '#0e9f6e', name: 'Green' },
  { color: '#ff8800', name: 'Orange' },
  { color: '#8b5cf6', name: 'Purple' },
  { color: '#ec4899', name: 'Pink' },
];

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [shortcutsVisible, setShortcutsVisible] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateTheme } = useAuthStore();

  const hasAccess = (moduleId) => user?.moduleList?.includes(moduleId);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Global keyboard shortcuts
  useKeyboardShortcuts([
    { key: '1', ctrl: true, action: () => navigate('/dashboard/masters/ready-products'), description: 'Go to Ready Products' },
    { key: '2', ctrl: true, action: () => navigate('/dashboard/masters/raw-materials'), description: 'Go to Raw Materials' },
    { key: '3', ctrl: true, action: () => navigate('/dashboard/masters/mappings'), description: 'Go to Mappings' },
    { key: '4', ctrl: true, action: () => navigate('/dashboard/masters/vendors'), description: 'Go to Vendors' },
    { key: '5', ctrl: true, action: () => navigate('/dashboard/transactions/purchase'), description: 'Go to Purchase' },
    { key: '6', ctrl: true, action: () => navigate('/dashboard/transactions/production'), description: 'Go to Production' },
    { key: '8', ctrl: true, action: () => navigate('/dashboard/admin/users'), description: 'Go to Users' },
    { key: 'k', ctrl: true, action: () => setPaletteOpen(true), description: 'Command palette' },
    { key: '?', action: () => setShortcutsVisible(true), description: 'Show shortcuts' },
    { key: 'Escape', action: () => setShortcutsVisible(false), description: 'Close modal' },
  ]);

  const menuItems = [
    {
      group: 'DASHBOARD',
      items: [
        { icon: <HomeOutlined />, label: 'Dashboard', path: '/dashboard' },
      ],
    },
    {
      group: 'MASTERS',
      items: [
        hasAccess(MODULE_IDS.READY_PRODUCT) && { icon: <TagOutlined />, label: 'Ready Products', path: '/dashboard/masters/ready-products' },
        hasAccess(MODULE_IDS.RAW_MATERIAL) && { icon: <InboxOutlined />, label: 'Raw Materials', path: '/dashboard/masters/raw-materials' },
        { icon: <CalculatorOutlined />, label: 'Unit Master', path: '/dashboard/masters/units' },
        hasAccess(MODULE_IDS.MAPPING) && { icon: <ApartmentOutlined />, label: 'Raw vs Ready', path: '/dashboard/masters/mappings' },
        hasAccess(MODULE_IDS.VENDOR) && { icon: <ShopOutlined />, label: 'Vendors', path: '/dashboard/masters/vendors' },
      ].filter(Boolean),
    },
    {
      group: 'TRANSACTIONS',
      items: [
        hasAccess(MODULE_IDS.RAW_PURCHASE) && { icon: <ShoppingCartOutlined />, label: 'Raw Purchase', path: '/dashboard/transactions/purchase' },
        hasAccess(MODULE_IDS.PRODUCTION) && { icon: <ToolOutlined />, label: 'Production Entry', path: '/dashboard/transactions/production' },
      ].filter(Boolean),
    },
    hasAccess(MODULE_IDS.REPORTS) && {
      group: 'REPORTS',
      items: [
        { icon: <BarChartOutlined />, label: 'Stock Summary', path: '/dashboard/reports/stock' },
        { icon: <FileTextOutlined />, label: 'Purchase Register', path: '/dashboard/reports/purchase' },
        { icon: <FileDoneOutlined />, label: 'Production Register', path: '/dashboard/reports/production' },
        { icon: <PieChartOutlined />, label: 'Consumption', path: '/dashboard/reports/consumption' },
      ],
    },
    hasAccess(MODULE_IDS.USER_MGMT) && {
      group: 'ADMIN',
      items: [
        { icon: <UserOutlined />, label: 'Users', path: '/dashboard/admin/users' },
        { icon: <SafetyOutlined />, label: 'Roles', path: '/dashboard/admin/roles' },
      ],
    },
  ].filter(Boolean);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleThemeChange = async (color) => {
    try {
      updateTheme(color);
      if (user?.plantId && user?.loginId) {
        await updateThemeApi(user.plantId, user.loginId, color);
      }
    } catch (error) {
      message.error('Failed to save theme');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    return [
      { title: <HomeOutlined />, path: '/dashboard' },
      ...paths.slice(1).map((path, idx) => ({
        title: (path || '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        path: '/' + paths.slice(0, idx + 2).join('/')
      }))
    ];
  };

  const userMenuItems = [
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: handleLogout }
  ];

  const sidebarContent = (
    <>
      {/* Logo Section */}
      <div style={{ height: 64, display: 'flex', alignItems: 'center', gap: 6, padding: '0 2px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <img 
          src={logo} 
          alt="Logo" 
          style={{ 
            width: collapsed && !isMobile ? 48 : 60, 
            height: collapsed && !isMobile ? 48 : 60, 
            objectFit: 'contain',
            borderRadius: 4
          }} 
        />
        {!(collapsed && !isMobile) && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap' }}>
              OceanBlue
            </Text>
            <Text style={{ color: '#64748b', fontSize: 12 }}>Inventory Management System</Text>
          </div>
        )}
      </div>

      {/* User Profile Section */}
      {!(collapsed && !isMobile) && (
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Avatar size={40} style={{ background: '#1a56db', fontSize: 16, fontWeight: 600 }}>
              {getInitials(user?.userName || user?.loginId)}
            </Avatar>
            <div>
              <Text style={{ color: '#fff', fontSize: 14, display: 'block' }}>{user?.userName || user?.loginId}</Text>
              <Text style={{ color: '#64748b', fontSize: 12 }}>{user?.plantName}</Text>
            </div>
          </Space>
        </div>
      )}

      {/* Menu Groups */}
      <div style={{ padding: '16px 0' }}>
        {menuItems.map((group, gIdx) => (
          <div key={gIdx} style={{ marginBottom: 16 }}>
            {!(collapsed && !isMobile) && (
              <Text style={{ color: '#475569', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '0 16px', display: 'block', marginBottom: 8 }}>
                {group.group}
              </Text>
            )}
            {group.items.map((item, iIdx) => {
              const isActive = location.pathname === item.path;
              const menuItem = (
                <div
                  key={iIdx}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileDrawerOpen(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    margin: '2px 8px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    color: isActive ? '#60a5fa' : '#cbd5e1',
                    background: isActive ? 'rgba(26,86,219,0.15)' : 'transparent',
                    borderLeft: isActive ? '3px solid #1a56db' : '3px solid transparent',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ fontSize: 16 }}>{item.icon}</span>
                  {!(collapsed && !isMobile) && <span style={{ fontSize: 14 }}>{item.label}</span>}
                </div>
              );
              
              return (collapsed && !isMobile) ? (
                <div key={iIdx} title={item.label}>
                  {menuItem}
                </div>
              ) : menuItem;
            })}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={256}
          style={{
            background: '#0f172a',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: 'auto'
          }}
        >
          {sidebarContent}
          {/* Collapse Toggle */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              position: 'absolute',
              right: -4,
              top: 72,
              width: 32,
              height: 32,
              background: '#1a56db',
              color: '#fff',
              borderRadius: '50%',
              border: '2px solid #0f172a',
              zIndex: 101
            }}
          />
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          closable={false}
          width={256}
          styles={{ body: { padding: 0, background: '#0f172a' } }}
        >
          {sidebarContent}
        </Drawer>
      )}


      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 256), transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            background: '#fff',
            padding: isMobile ? '0 12px' : '0 24px',
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            boxShadow: '0 1px 0 #e2e8f0'
          }}
        >
          {/* Left: Menu Button (Mobile) or Breadcrumb */}
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileDrawerOpen(true)}
              style={{ fontSize: 20 }}
            />
          ) : (
            <Breadcrumb
              items={getBreadcrumbs().map(b => ({
                title: b.title,
                onClick: () => navigate(b.path),
                style: { cursor: 'pointer' }
              }))}
            />
          )}

          {/* Right: Actions */}
          <Space size={isMobile ? 8 : 12}>
            {!isMobile && (
              <>
                <Button
                  type="text"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setShortcutsVisible(true)}
                  style={{ color: '#64748b' }}
                />
                <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => setPaletteOpen(true)}>Ctrl+K</Tag>
                <Space size={4}>
                  {THEME_COLORS.map(t => (
                    <div
                      key={t.color}
                      onClick={() => handleThemeChange(t.color)}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: t.color,
                        cursor: 'pointer',
                        border: user?.themeColor === t.color ? '2px solid #0f172a' : '2px solid transparent',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  ))}
                </Space>
              </>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                {!isMobile && <Text strong>{user?.userName || user?.loginId}</Text>}
                <Avatar size={32} style={{ background: user?.themeColor || '#1a56db' }}>
                  {getInitials(user?.userName || user?.loginId)}
                </Avatar>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ background: '#f8fafc', padding: isMobile ? 12 : 24, minHeight: 'calc(100vh - 64px)' }}>
          <div className="page-enter">
            <Outlet />
          </div>
        </Content>
      </Layout>

      {/* Command Palette */}
      <CommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        currentPageActions={[]}
      />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        visible={shortcutsVisible}
        onClose={() => setShortcutsVisible(false)}
      />
    </Layout>
  );
};

export default AppLayout;